<template>
  <div class="planning-calendar">
    <div class="calendar-toolbar">
      <div class="calendar-navigation">
        <button
          type="button"
          class="calendar-nav-button"
          :aria-label="$t('previous')"
          :title="$t('previous')"
          @click="previousMonth"
        >
          <AppIcon :name="$i18n.locale === 'ar' ? 'bi bi-chevron-right' : 'bi bi-chevron-left'" :class="$i18n.locale === 'ar' ? 'bi bi-chevron-right' : 'bi bi-chevron-left'" aria-hidden="true" />
        </button>

        <div class="calendar-heading">
          <h5 class="mb-0">{{ currentMonthYear }}</h5>
          <span class="calendar-event-count">
            <AppIcon name="bi-calendar2-check" class="bi bi-calendar2-check" aria-hidden="true" />
            {{ eventsInMonth }}
          </span>
        </div>

        <button
          type="button"
          class="calendar-nav-button"
          :aria-label="$t('next')"
          :title="$t('next')"
          @click="nextMonth"
        >
          <AppIcon :name="$i18n.locale === 'ar' ? 'bi bi-chevron-left' : 'bi bi-chevron-right'" :class="$i18n.locale === 'ar' ? 'bi bi-chevron-left' : 'bi bi-chevron-right'" aria-hidden="true" />
        </button>
      </div>

      <button type="button" class="btn btn-sm btn-primary calendar-today-button" @click="goToToday">
        <AppIcon name="bi-calendar-event" class="bi bi-calendar-event me-1" aria-hidden="true" />
        {{ $t('today') }}
      </button>
    </div>

    <div class="calendar-legend" :aria-label="$t('planning_status_legend')">
      <span v-for="status in statuses" :key="status.value" class="legend-item">
        <span class="legend-dot" :style="{ backgroundColor: status.color }"></span>
        {{ $t(status.value) }}
      </span>
    </div>

    <div class="calendar-scroll" tabindex="0">
      <div class="calendar-grid">
        <div v-for="day in dayNames" :key="day" class="calendar-day-header">
          {{ $t(day.toLowerCase()) }}
        </div>

        <div
          v-for="day in calendarDays"
          :key="day.key"
          :class="['calendar-day', {
            'other-month': !day.isCurrentMonth,
            'today': day.isToday,
            'has-events': day.totalPlannings > 0
          }]"
          @click="selectDay(day)"
        >
          <div class="day-number-container">
            <span class="day-number">{{ day.date }}</span>
            <span v-if="day.totalPlannings" class="day-event-count">{{ day.totalPlannings }}</span>
          </div>

          <div class="day-content">
            <button
              v-for="planning in day.plannings"
              :key="planning.id"
              type="button"
              class="planning-event"
              :style="eventStyle(planning.status)"
              :aria-label="eventAriaLabel(planning)"
              @click.stop="$emit('open-detail', planning)"
            >
              <span class="planning-event-time">
                {{ planning.start_time || '--:--' }}
              </span>
              <span class="planning-event-title">{{ planning.title }}</span>
              <span v-if="planning.assignedUser" class="planning-event-person">
                <AppIcon name="bi-person" class="bi bi-person" aria-hidden="true" />
                {{ planning.assignedUser.name }}
              </span>
              <span v-if="planning.client" class="planning-event-client">
                <AppIcon name="bi-building" class="bi bi-building" aria-hidden="true" />
                {{ planning.client.name }}
              </span>
            </button>

            <div v-if="day.totalPlannings > visibleEventsPerDay" class="more-indicator">
              +{{ day.totalPlannings - visibleEventsPerDay }} {{ $t('more') }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'

export default {
  name: 'MyPlanningCalendar',
  props: {
    plannings: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      currentDate: new Date(),
      visibleEventsPerDay: 3,
      statuses: [
        { value: 'planned', color: '#0d6efd', background: '#eef5ff' },
        { value: 'in_progress', color: '#0ea5e9', background: '#eefaff' },
        { value: 'completed', color: '#198754', background: '#effaf4' },
        { value: 'failed', color: '#dc3545', background: '#fff1f2' },
        { value: 'postponed', color: '#d97706', background: '#fff8e8' }
      ]
    }
  },
  computed: {
    weekStartsOn() {
      return this.$i18n.locale === 'ar' ? 6 : 1
    },
    dayNames() {
      const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
      return Array.from({ length: 7 }, (_, index) => names[(this.weekStartsOn + index) % 7])
    },
    currentMonthYear() {
      return new Intl.DateTimeFormat(this.calendarLocale, {
        month: 'long',
        year: 'numeric'
      }).format(this.currentDate)
    },
    calendarLocale() {
      return {
        ar: 'ar-MA',
        fr: 'fr-FR',
        en: 'en-US'
      }[this.$i18n.locale] || this.$i18n.locale || 'en-US'
    },
    eventsInMonth() {
      const monthKey = moment(this.currentDate).format('YYYY-MM')
      return this.plannings.filter(planning => String(planning.date || '').startsWith(monthKey)).length
    },
    calendarDays() {
      const year = this.currentDate.getFullYear()
      const month = this.currentDate.getMonth()
      const firstDay = new Date(year, month, 1)
      const firstDayOffset = (firstDay.getDay() - this.weekStartsOn + 7) % 7
      const lastDate = new Date(year, month + 1, 0).getDate()
      const previousMonthLastDate = new Date(year, month, 0).getDate()
      const days = []

      for (let offset = firstDayOffset - 1; offset >= 0; offset--) {
        days.push(this.buildCalendarDay(new Date(year, month - 1, previousMonthLastDate - offset), false))
      }

      for (let date = 1; date <= lastDate; date++) {
        days.push(this.buildCalendarDay(new Date(year, month, date), true))
      }

      const remainingDays = 42 - days.length
      for (let date = 1; date <= remainingDays; date++) {
        days.push(this.buildCalendarDay(new Date(year, month + 1, date), false))
      }

      return days
    }
  },
  mounted() {
    this.emitMonthChange()
  },
  methods: {
    buildCalendarDay(fullDate, isCurrentMonth) {
      return {
        key: moment(fullDate).format('YYYY-MM-DD'),
        date: fullDate.getDate(),
        fullDate,
        isCurrentMonth,
        isToday: moment(fullDate).isSame(moment(), 'day'),
        ...this.getPlanningsDataForDate(fullDate)
      }
    },
    getPlanningsDataForDate(date) {
      const dateKey = moment(date).format('YYYY-MM-DD')
      const dayPlannings = this.plannings
        .filter(planning => planning.date === dateKey)
        .sort((first, second) => String(first.start_time || '').localeCompare(String(second.start_time || '')))

      return {
        plannings: dayPlannings.slice(0, this.visibleEventsPerDay),
        totalPlannings: dayPlannings.length
      }
    },
    getStatus(status) {
      return this.statuses.find(item => item.value === status) || {
        color: '#6c757d',
        background: '#f4f5f6'
      }
    },
    eventStyle(status) {
      const statusConfig = this.getStatus(status)
      return {
        borderInlineStartColor: statusConfig.color,
        backgroundColor: statusConfig.background
      }
    },
    eventAriaLabel(planning) {
      const parts = [
        planning.title,
        planning.start_time,
        planning.client && planning.client.name,
        this.$t(planning.status)
      ]
      return parts.filter(Boolean).join(', ')
    },
    selectDay(day) {
      this.$emit('create-planning', day.key)
    },
    setCurrentMonth(date) {
      this.currentDate = date
      this.$nextTick(this.emitMonthChange)
    },
    previousMonth() {
      this.setCurrentMonth(new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() - 1, 1))
    },
    nextMonth() {
      this.setCurrentMonth(new Date(this.currentDate.getFullYear(), this.currentDate.getMonth() + 1, 1))
    },
    goToToday() {
      this.setCurrentMonth(new Date())
    },
    emitMonthChange() {
      const month = moment(this.currentDate)
      this.$emit('month-change', {
        start_date: month.clone().startOf('month').format('YYYY-MM-DD'),
        end_date: month.clone().endOf('month').format('YYYY-MM-DD')
      })
    }
  }
}
</script>

<style scoped>
.planning-calendar {
  width: 100%;
  padding: 1rem;
  background: #fff;
  border: 1px solid #e8ebef;
  border-radius: 16px;
  box-shadow: 0 10px 30px rgba(31, 41, 55, 0.05);
}

.calendar-toolbar,
.calendar-navigation,
.calendar-heading,
.calendar-legend,
.legend-item,
.day-number-container {
  display: flex;
  align-items: center;
}

.calendar-toolbar {
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
}

.calendar-navigation {
  gap: 0.65rem;
}

.calendar-heading {
  gap: 0.65rem;
  min-width: 220px;
  justify-content: center;
}

.calendar-heading h5 {
  color: #172033;
  font-weight: 750;
  text-transform: capitalize;
}

.calendar-event-count {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3rem 0.55rem;
  border-radius: 999px;
  background: #f1f4f8;
  color: #667085;
  font-size: 0.72rem;
  font-weight: 700;
}

.calendar-nav-button {
  width: 34px;
  height: 34px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #dfe3e8;
  border-radius: 10px;
  background: #fff;
  color: #344054;
  transition: all 0.18s ease;
}

.calendar-nav-button:hover,
.calendar-nav-button:focus-visible {
  color: #0d6efd;
  border-color: #9ec5fe;
  background: #f3f7ff;
  outline: none;
  box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
}

.calendar-today-button {
  border-radius: 10px;
  padding: 0.45rem 0.85rem;
}

.calendar-legend {
  gap: 0.85rem;
  flex-wrap: wrap;
  padding: 0.65rem 0.8rem;
  margin-bottom: 0.9rem;
  background: #f8fafc;
  border-radius: 10px;
}

.legend-item {
  gap: 0.35rem;
  color: #667085;
  font-size: 0.72rem;
  font-weight: 600;
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.calendar-scroll {
  overflow-x: auto;
  border: 1px solid #e8ebef;
  border-radius: 12px;
}

.calendar-scroll:focus-visible {
  outline: 3px solid rgba(13, 110, 253, 0.15);
  outline-offset: 2px;
}

.calendar-grid {
  min-width: 900px;
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 1px;
  background: #e9edf2;
}

.calendar-day-header {
  padding: 0.75rem;
  background: #f7f9fc;
  color: #667085;
  text-align: center;
  font-size: 0.7rem;
  font-weight: 750;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.calendar-day {
  min-height: 145px;
  padding: 0.55rem;
  background: #fff;
  cursor: pointer;
  transition: background-color 0.18s ease;
}

.calendar-day:hover {
  background: #f8fbff;
}

.calendar-day.other-month {
  background: #fafbfc;
}

.calendar-day.other-month .day-number,
.calendar-day.other-month .planning-event {
  opacity: 0.55;
}

.calendar-day.today {
  background: #f7faff;
  box-shadow: inset 0 0 0 1px #9ec5fe;
}

.day-number-container {
  justify-content: space-between;
  min-height: 26px;
  margin-bottom: 0.4rem;
}

.day-number {
  width: 26px;
  height: 26px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  color: #475467;
  font-size: 0.78rem;
  font-weight: 700;
}

.calendar-day.today .day-number {
  background: #0d6efd;
  color: #fff;
}

.day-event-count {
  color: #98a2b3;
  font-size: 0.65rem;
  font-weight: 700;
}

.day-content {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.planning-event {
  width: 100%;
  min-width: 0;
  padding: 0.42rem 0.48rem;
  border: 0;
  border-inline-start: 3px solid;
  border-radius: 7px;
  color: #344054;
  text-align: start;
  transition: transform 0.16s ease, box-shadow 0.16s ease;
}

.planning-event:hover,
.planning-event:focus-visible {
  transform: translateY(-1px);
  outline: none;
  box-shadow: 0 4px 12px rgba(31, 41, 55, 0.12);
}

.planning-event-time,
.planning-event-person,
.planning-event-client {
  display: block;
  color: #667085;
  font-size: 0.62rem;
  line-height: 1.3;
}

.planning-event-time {
  font-weight: 700;
}

.planning-event-title {
  display: block;
  overflow: hidden;
  margin: 0.08rem 0;
  color: #1d2939;
  font-size: 0.72rem;
  font-weight: 700;
  line-height: 1.35;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.planning-event-person,
.planning-event-client {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.planning-event-client {
  color: #475467;
  font-weight: 600;
}

.more-indicator {
  padding: 0.15rem 0.35rem;
  color: #0d6efd;
  font-size: 0.67rem;
  font-weight: 700;
}

@media (max-width: 767.98px) {
  .planning-calendar {
    padding: 0.75rem;
    border-radius: 12px;
  }

  .calendar-toolbar {
    align-items: stretch;
    flex-direction: column;
  }

  .calendar-navigation {
    justify-content: space-between;
  }

  .calendar-heading {
    min-width: 0;
  }

  .calendar-today-button {
    align-self: flex-start;
  }

  .calendar-day {
    min-height: 130px;
  }
}
</style>
